import { createSelector } from '@reduxjs/toolkit'
import {
  createFileUpload,
  getFileUpload,
  processFileUpload,
  SourcePath,
} from 'actions/fileUpload'
import { State } from 'models/FileUpload'
import { ValidationTypes } from 'models/Validation'
import { selectApp } from 'selectors/app'
import { selectThemeId } from 'selectors/landingPageTheme'
import { selectOrderingEnabled } from 'selectors/merchant'
import { api } from './index'

// API-specific types for payloads
export interface NavigationTabFileUpload {
  file_upload_id?: number
  destroy?: boolean
}

export interface NavigationTabInput {
  id?: number
  name: string
  link: string
  position: number
  brame_campaign_id?: number
  file_uploads?: {
    default_icon?: NavigationTabFileUpload
    selected_icon?: NavigationTabFileUpload
  }
  // Extended properties to support image uploads
  default_icon?: {
    url?: string
    file_upload_id?: number
  }
  selected_icon?: {
    url?: string
    file_upload_id?: number
  }
}

// API response interface (includes server-only fields)
export interface NavigationTabApiResponse {
  id: number
  name: string
  link: string // API returns link as string, not UrlValue
  position: number
  brame_campaign_id?: number
  brame_campaign_url?: string
  default_icon?: {
    id?: number // Make id optional since client-side defaults don't have hardcoded IDs
    url: string
  }
  selected_icon?: {
    id?: number // Make id optional since client-side defaults don't have hardcoded IDs
    url: string
  }
  created_at?: string
  updated_at?: string
}

export interface DefaultNavigationTabApiResponse
  extends Omit<NavigationTabApiResponse, 'id'> {}

export interface NavigationTabsUpdatePayload {
  appId: number
  themeId: number
  navigation_tabs: NavigationTabInput[]
}

// New response format that includes both navigation_tabs and default_tabs
export interface NavigationTabsResponse {
  navigation_tabs: NavigationTabApiResponse[]
  default_tabs: NavigationTabApiResponse[]
}

// Combined response format for the slice (using API response format with string links)
export interface CombinedNavigationTabsData {
  navigation_tabs: NavigationTabApiResponse[]
  default_tabs: NavigationTabApiResponse[]
}

// Helper function to check if URL is a local asset
const isLocalAssetUrl = (url: string): boolean => {
  return (
    url.startsWith('/static/') ||
    url.startsWith('data:') ||
    url.includes('webpack')
  )
}

/**
 * Uploads a single icon file and returns the record_id
 * @param url - The URL of the image to upload
 * @param merchantId - The merchant ID for the upload
 * @param dispatch - Redux dispatch function
 * @param iconType - The type of icon being uploaded ('default' or 'selected')
 * @returns The record_id if successful, null if failed
 */
const uploadIcon = async (
  url: string,
  merchantId: number,
  dispatch: any,
  iconType: 'default' | 'selected' = 'default'
): Promise<number | null> => {
  try {
    // Step 1: Fetch the image data first to get the file type
    const imageResponse = await fetch(url)
    if (!imageResponse.ok) {
      return null
    }
    const imageBlob = await imageResponse.blob()

    // Extract file extension from content type or URL
    const contentType = imageBlob.type || 'image/png'
    const fileExtension = contentType.split('/').pop() || 'png'

    // Step 2: Create file upload record using Redux action (matching ImagePicker logic)
    const recordType =
      iconType === 'default'
        ? ValidationTypes.NavigationTabDefaultIcon
        : ValidationTypes.NavigationTabSelectedIcon

    const createResponse = await dispatch(
      createFileUpload(SourcePath.MERCHANTS, merchantId, {
        file_type: fileExtension,
        settings: {
          relation_name: 'image',
        },
        record_type: recordType, // Use appropriate validation type based on icon type
      })
    )

    if (createResponse.type === 'fileUpload/CREATE_FAIL') {
      return null
    }

    const uploadRecord = createResponse.payload.data.file_upload

    // Step 3: Upload to S3 using axios.put (matching ImagePicker logic)
    if (!uploadRecord.upload_url) {
      return null
    }

    const uploadResponse = await fetch(uploadRecord.upload_url, {
      method: 'PUT',
      headers: { 'content-type': contentType },
      body: imageBlob,
    })

    if (!uploadResponse.ok) {
      return null
    }

    // Step 4: Process the upload using Redux action
    const processResponse = await dispatch(
      processFileUpload(
        SourcePath.MERCHANTS,
        uploadRecord.source_id,
        uploadRecord.id
      )
    )

    if (processResponse.type === 'fileUpload/PROCESS_FAIL') {
      return null
    }

    // Step 5: Poll until the upload is finalized (matching StandaloneImagePicker behavior)
    let fileUpload = processResponse.payload.data.file_upload
    const maxAttempts = 60 // 5 minutes max (60 * 5 seconds)
    let attempts = 0

    while (fileUpload.state !== State.FINALIZED && attempts < maxAttempts) {
      // Wait 5 seconds before checking again
      await new Promise(resolve => setTimeout(resolve, 5000))

      const getResponse = await dispatch(
        getFileUpload(SourcePath.MERCHANTS, merchantId, fileUpload.id)
      )

      if (getResponse.type === 'fileUpload/GET_FAIL') {
        return null
      }

      fileUpload = getResponse.payload.data.file_upload

      if (fileUpload.state === State.ERRORED) {
        return null
      }

      attempts++
    }

    // Return null if we timed out waiting for finalization
    if (fileUpload.state !== State.FINALIZED) {
      return null
    }

    // Return the record_id, not the file_upload.id
    return fileUpload.record_id
  } catch (error) {
    return null
  }
}

/**
 * Checks if any navigation tabs have default icons that need uploading
 * @param navigationTabs - Array of navigation tab inputs
 * @returns True if any tabs have default icons without file_upload_id
 */
export const hasDefaultIcons = (
  navigationTabs: NavigationTabInput[]
): boolean => {
  return navigationTabs.some(tab => {
    const hasDefaultIconToUpload =
      tab.default_icon?.url &&
      !tab.default_icon.file_upload_id &&
      !tab.id &&
      isLocalAssetUrl(tab.default_icon.url)

    const hasSelectedIconToUpload =
      tab.selected_icon?.url &&
      !tab.selected_icon.file_upload_id &&
      !tab.id &&
      isLocalAssetUrl(tab.selected_icon.url)

    return hasDefaultIconToUpload || hasSelectedIconToUpload
  })
}

/**
 * Pre-uploads any default icons that need uploading and returns updated payload
 * @param payload - The original navigation tabs update payload
 * @param merchantId - The merchant ID for the upload
 * @param dispatch - Redux dispatch function
 * @returns Updated payload with file_upload_ids for uploaded icons
 */
const preUploadDefaultIcons = async (
  payload: NavigationTabsUpdatePayload,
  merchantId: number,
  dispatch: any
): Promise<NavigationTabsUpdatePayload> => {
  const processedPayload = {
    ...payload,
    navigation_tabs: [...payload.navigation_tabs],
  }

  // Collect all upload promises to run in parallel
  const uploadPromises: Promise<void>[] = []

  // Process each navigation tab
  for (let i = 0; i < processedPayload.navigation_tabs.length; i++) {
    const tab = processedPayload.navigation_tabs[i]
    const updatedTab = { ...tab }

    // Handle default_icon upload
    if (
      tab.default_icon?.url &&
      !tab.default_icon.file_upload_id &&
      !tab.id &&
      isLocalAssetUrl(tab.default_icon.url)
    ) {
      const uploadPromise = uploadIcon(
        tab.default_icon.url,
        merchantId,
        dispatch,
        'default'
      ).then(recordId => {
        if (recordId) {
          // Initialize file_uploads if it doesn't exist
          if (!updatedTab.file_uploads) {
            updatedTab.file_uploads = {}
          }
          updatedTab.file_uploads.default_icon = { file_upload_id: recordId }
        }
      })
      uploadPromises.push(uploadPromise)
    } else if (tab.default_icon?.file_upload_id) {
      // Copy existing file_upload_id
      if (!updatedTab.file_uploads) {
        updatedTab.file_uploads = {}
      }
      updatedTab.file_uploads.default_icon = {
        file_upload_id: tab.default_icon.file_upload_id,
      }
    }

    // Handle selected_icon upload
    if (
      tab.selected_icon?.url &&
      !tab.selected_icon.file_upload_id &&
      !tab.id &&
      isLocalAssetUrl(tab.selected_icon.url)
    ) {
      const uploadPromise = uploadIcon(
        tab.selected_icon.url,
        merchantId,
        dispatch,
        'selected'
      ).then(recordId => {
        if (recordId) {
          // Initialize file_uploads if it doesn't exist
          if (!updatedTab.file_uploads) {
            updatedTab.file_uploads = {}
          }
          updatedTab.file_uploads.selected_icon = { file_upload_id: recordId }
        }
      })
      uploadPromises.push(uploadPromise)
    } else if (tab.selected_icon?.file_upload_id) {
      // Copy existing file_upload_id
      if (!updatedTab.file_uploads) {
        updatedTab.file_uploads = {}
      }
      updatedTab.file_uploads.selected_icon = {
        file_upload_id: tab.selected_icon.file_upload_id,
      }
    }

    // Remove the extended properties before sending to API
    delete updatedTab.default_icon
    delete updatedTab.selected_icon

    processedPayload.navigation_tabs[i] = updatedTab
  }

  // Wait for all uploads to complete (either successfully or unsuccessfully)
  await Promise.allSettled(uploadPromises)

  return processedPayload
}

export const navigationTabsApi = api.injectEndpoints({
  endpoints: build => ({
    getNavigationTabs: build.query<
      CombinedNavigationTabsData,
      { appId: number; themeId: number }
    >({
      query: ({ appId, themeId }) => ({
        url: `apps/${appId}/landing_page_themes/${themeId}/navigation_tabs`,
        method: 'GET',
      }),
      providesTags: ['NavigationTabs'],
    }),
    updateNavigationTabs: build.mutation<
      NavigationTabApiResponse[],
      NavigationTabsUpdatePayload
    >({
      query: payload => ({
        url: `apps/${payload.appId}/landing_page_themes/${payload.themeId}/navigation_tabs`,
        method: 'PUT',
        body: {
          navigation_tabs: payload.navigation_tabs,
        },
      }),
      invalidatesTags: ['NavigationTabs', 'LandingPageTheme'],
    }),
  }),
})

export const { useGetNavigationTabsQuery, useUpdateNavigationTabsMutation } =
  navigationTabsApi

// Export the helper functions for use in consuming components
export { preUploadDefaultIcons, uploadIcon }

// Selectors for loading states using createSelector
export const selectNavigationTabsIsLoading = createSelector(
  [selectApp, selectThemeId, state => state],
  (app, themeId, state) => {
    if (!app?.id || !themeId) return false
    const queryState = navigationTabsApi.endpoints.getNavigationTabs.select({
      appId: app.id,
      themeId,
    })(state)
    return (queryState as any).isFetching || false
  }
)

export const selectNavigationTabsIsUninitialized = createSelector(
  [selectApp, selectThemeId, state => state],
  (app, themeId, state) => {
    if (!app?.id || !themeId) return true
    const queryState = navigationTabsApi.endpoints.getNavigationTabs.select({
      appId: app.id,
      themeId,
    })(state)
    return (queryState as any).isUninitialized || false
  }
)

export const selectNavigationTabsIsSubmitting = createSelector(
  [
    state => state.api?.mutations || {},
    state => state.appNavigation?.bottomNav?.isSubmitting || false,
  ],
  (mutations, bottomNavSubmitting) => {
    const apiSubmitting = Object.values(mutations).some(
      (mutation: any) =>
        mutation?.endpointName === 'updateNavigationTabs' &&
        mutation?.status === 'pending'
    )
    return apiSubmitting || bottomNavSubmitting
  }
)

export const selectNavigationTabsMutationData = createSelector(
  [state => state.api?.mutations || {}],
  mutations => {
    // Find the most recent successful updateNavigationTabs mutation
    const mutationEntries = Object.values(mutations).filter(
      (mutation: any) =>
        mutation?.endpointName === 'updateNavigationTabs' &&
        mutation?.status === 'fulfilled' &&
        mutation?.data
    )
    // Return the most recent mutation data with timestamp (mutations are keyed by timestamp-like IDs)
    if (mutationEntries.length > 0) {
      const latestMutation = mutationEntries[mutationEntries.length - 1] as any
      return {
        data: latestMutation.data,
        fulfilledTimeStamp: latestMutation.fulfilledTimeStamp,
      }
    }
    return null
  }
)

export const selectNavigationTabsApiData = createSelector(
  [selectApp, selectThemeId, selectOrderingEnabled, state => state],
  (app, themeId, orderingEnabled, state) => {
    if (!app?.id || !themeId) return undefined

    const queryState = navigationTabsApi.endpoints.getNavigationTabs.select({
      appId: app.id,
      themeId,
    })(state)

    const apiData = (queryState as any)?.data
    const navigationTabs = apiData?.navigation_tabs || []

    // Default tabs are hardcoded, generated by merchant.app_ordering_enabled
    const defaultTabs = getDefaultNavigationTabs(orderingEnabled)

    return {
      navigation_tabs: navigationTabs,
      default_tabs: defaultTabs,
    }
  }
)

// Helper function to get position from key (matches Ruby logic)
const positionFromKey = (key: string, orderingEnabled: boolean): number => {
  switch (key) {
    case 'home':
      return 1
    case 'rewards':
      return 2
    case 'order':
      return 3
    case 'earn':
      return orderingEnabled ? 4 : 3
    case 'more':
      return orderingEnabled ? 5 : 4
    default:
      return 3 // fallback for unknown keys
  }
}

// Default navigation tabs configuration (using local assets)
const DEFAULT_NAV_TAB_CONFIGS = {
  home: {
    key: 'home',
    name: 'Home',
    link: 'thanx://home',
    default_icon: {
      url: require('assets/images/cms/app-navigation/bottom-nav-defaults/home@3x.png'),
    },
    selected_icon: {
      url: require('assets/images/cms/app-navigation/bottom-nav-defaults/home@3x.png'),
    },
  },
  rewards: {
    key: 'rewards',
    name: 'Rewards',
    link: 'thanx://rewards',
    default_icon: {
      url: require('assets/images/cms/app-navigation/bottom-nav-defaults/rewards@3x.png'),
    },
    selected_icon: {
      url: require('assets/images/cms/app-navigation/bottom-nav-defaults/rewards@3x.png'),
    },
  },
  order: {
    key: 'order',
    name: 'Order',
    link: 'thanx://ordering-menu',
    default_icon: {
      url: require('assets/images/cms/app-navigation/bottom-nav-defaults/order@3x.png'),
    },
    selected_icon: {
      url: require('assets/images/cms/app-navigation/bottom-nav-defaults/order@3x.png'),
    },
  },
  earn: {
    key: 'earn',
    name: 'Earn',
    link: 'thanx://earn',
    default_icon: {
      url: require('assets/images/cms/app-navigation/bottom-nav-defaults/earn@3x.png'),
    },
    selected_icon: {
      url: require('assets/images/cms/app-navigation/bottom-nav-defaults/earn@3x.png'),
    },
  },
  more: {
    key: 'more',
    name: 'More',
    link: 'thanx://drawer',
    default_icon: {
      url: require('assets/images/cms/app-navigation/bottom-nav-defaults/more@3x.png'),
    },
    selected_icon: {
      url: require('assets/images/cms/app-navigation/bottom-nav-defaults/more@3x.png'),
    },
  },
}

/**
 * Returns the default navigation tabs directly in JavaScript
 * This replaces the Ruby `default_navigation_tabs` method logic
 * @param orderingEnabled - Whether ordering is enabled for the merchant
 * @returns Array of default navigation tab objects
 */
export const getDefaultNavigationTabs = (
  orderingEnabled: boolean = true
): NavigationTabApiResponse[] | DefaultNavigationTabApiResponse[] => {
  return Object.values(DEFAULT_NAV_TAB_CONFIGS)
    .filter(config => {
      // Skip 'order' tab if ordering is not enabled (matches Ruby logic)
      if (config.key === 'order' && !orderingEnabled) {
        return false
      }
      return true
    })
    .map(config => ({
      name: config.name,
      link: config.link,
      // Position needs to be adjusted for the order tab missing when !orderingEnabled
      position: positionFromKey(config.key, orderingEnabled),
      default_icon: {
        // Don't set id for client-side defaults - will be set by server if needed
        url: config.default_icon.url,
      },
      selected_icon: {
        // Don't set id for client-side defaults - will be set by server if needed
        url: config.selected_icon.url,
      },
      created_at: undefined, // Default tabs don't have creation dates
      updated_at: undefined, // Default tabs don't have update dates
    }))
    .sort((a, b) => a.position - b.position) // Ensure proper ordering
}
